import React, { useState, useEffect } from 'react';
import { Table, Tooltip } from 'reactstrap';
import styled from 'styled-components';
import { grayShades } from 'styles/abstracts/colors';
import { fontSizes } from 'styles/abstracts/typography';
import { withTranslation } from 'react-i18next';
import {
  StyledDropdown,
  StyledDropdownToggle,
  Item,
  ToggleButton,
  Menu,
  Label,
} from '_Cards/FilterCards/DataByFiltersCard/_Containers/Dropdowns/utils/DropdownStyles';
import Icon from '_Components/Icons/Icon';
import { ImageSize } from 'styles/abstracts/variables';
import { cloneDeep, get, flatten, orderBy, filter } from 'lodash';
import GET_THEMATICS_OVERVIEW_DATA_QUERY from '_Cards/ThematicsCard/ThematicsOverviewCards/graphql/getThematicsOverviewData';
import { useQuery } from 'react-apollo';
import { connect } from 'react-redux';
import Loader from '_Components/Loader/OnLoad';
import { selectThematicSelection } from '_Resources/Cards/Actions/selectThematicSelection';
import IrritantsAndEnchantingPointsAIRow from './IrritantsAndEnchantingPointsAIRow';

const TableContainer = styled.div`
  overflow-y: scroll;
  max-height: 62.5vh;
`;

const Head = styled.thead`
  border-bottom: 1px solid ${grayShades.g300};
  border-top: 1px solid ${grayShades.g300};
`;

const StyledHeaderCell = styled.th`
  color: ${grayShades.g600};
  font-size: ${fontSizes.default};
  padding: 1em !important;
`;

const TitleCell = styled(StyledHeaderCell)`
  width: 33em !important;
  vertical-align: middle !important;
`;

const IndicatorCell = styled(StyledHeaderCell)`
  width: 9em !important;
  text-align: center;
  vertical-align: middle !important;
`;

const UpRightSpan = styled.span`
  position: relative;
  top: -0.5em;
  font-size: 0.75em;
`;

const SubHeaderContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-around;
  align-items: center;
  margin-top: 5px;
  margin-bottom: 1rem;
`;

const SelectorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 5px;
  margin-bottom: 1rem;
`;

const StyledLabel = styled(Label)`
  margin-right: 1rem;
`;

const StyledToggleButton = styled(ToggleButton)`
  width: 14rem;
`;

const SortIcon = styled(Icon)`
  visibility: ${({ isVisible }) => !isVisible && 'hidden'};
`;

type ThematicSelectionType = {
  createdBy: {
    firstName: string;
    lastName: string;
    email: string;
  };
  id: number | null;
  label: string;
  name: string;
  projectId: number;
  thematics: Array<{
    thematic: string;
    subThematic: string | null;
    subSubThematic: string | null;
  }>;
};

type IrritantsAndEnchantingPointAIComputingCardType = {
  t: Function;
  filterKeys: any;
  projectId: number;
  range: {
    startDate: Date;
    endDate: Date;
  };
  aiUsageExpirationDate: Date;
  selectionList: Array<ThematicSelectionType>;
  dispatchSelectThematicSelection: Function;
  selectedThematicSelectionOption: ThematicSelectionType;
  currentMentionCountWithoutNull: number | null;
};

const IrritantsAndEnchantingPointAIComputingCard = ({
  t,
  filterKeys,
  projectId,
  range,
  aiUsageExpirationDate,
  selectionList,
  dispatchSelectThematicSelection,
  selectedThematicSelectionOption,
  currentMentionCountWithoutNull,
}: IrritantsAndEnchantingPointAIComputingCardType) => {
  const filtersToDisplay = [
    {
      id: 'thematics',
      label: 'Thematics',
    },
    {
      id: 'subThematics',
      label: 'Sub thematic',
    },
    {
      id: 'subSubThematics',
      label: 'Sub sub thematic',
    },
  ];
  const allThematics: ThematicSelectionType = {
    createdBy: {
      firstName: 'system',
      lastName: 'system',
      email: 'support@qemotion.com',
    },
    id: null,
    label: t('button:All thematics'),
    name: t('button:All thematics'),
    projectId,
    thematics: [],
  };
  const [isLevelDropdownOpen, setLevelDropdownOpen] = useState(false);
  const [isThematicSelectionDropdownOpen, setThematicSelectionDropdownOpen] = useState(false);
  const [currentThematicSelection, setCurrentThematicSelection] = useState<ThematicSelectionType>(
    selectedThematicSelectionOption ?? allThematics,
  );
  const [queryFilters, setQueryFilters] = useState({});
  const [thematicsData, setThematicsData] = useState<any[]>([]);
  const [totalThematicsMentionCount, setTotalThematicsMentionCount] = useState<number | null>(0);
  const [currentLevel, setCurrentLevel] = useState<{ id: string; label: string }>(
    JSON.parse(localStorage.getItem('landingPageAIIrritantsLevel')) || {
      id: 'thematics',
      label: 'Thematics',
    },
  );
  const [irritantsModal, setIrritantsModal] = useState(false);
  const [enchantementsModal, setEnchantementsModal] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [targetId, setTargetId] = useState(null);
  const [dataId, setDataId] = useState(null);
  const isAiAvailable = aiUsageExpirationDate && Date.now() < new Date(aiUsageExpirationDate);
  const [shouldFetch, setShouldFetch] = useState(false);
  const [orderDirection, setOrderDirection] = useState<boolean | 'desc' | 'asc' | undefined>('desc'); // asc or desc or false
  const [order, setOrder] = useState<string | null>(null); // name, absImpactSat, mentionCount or vb_percentage
  const [filterByLevelDone, setFilterByLevelDone] = useState(false);
  const { data: thematicsOverviewData, loading } = useQuery(GET_THEMATICS_OVERVIEW_DATA_QUERY, {
    variables: {
      ...queryFilters,
      projectId,
      range,
    },
    fetchPolicy: 'cache-and-network',
  });
  const [thematicSelectionOptions, setThematicSelectionOptions] = useState<ThematicSelectionType[]>([allThematics]);

  const handleFetchClick = (thematic: { thematicsOverviewDetails: { name: React.SetStateAction<null> } }) => {
    setShouldFetch(true);
    setDataId(thematic?.thematicsOverviewDetails?.name);
  };

  const getThematicAnalyzed = (thematic: {
    parentObject: { thematic: any; subThematic: any };
    thematicsOverviewDetails: { name: any };
  }) => {
    let item = {
      thematic: thematic?.thematicsOverviewDetails?.name,
      subThematic: null,
      subSubThematic: null,
    };
    switch (currentLevel.id) {
      case 'subThematics':
        item = {
          thematic: thematic?.parentObject?.thematic,
          subThematic: thematic?.thematicsOverviewDetails?.name,
          subSubThematic: null,
        };
        break;
      case 'subSubThematics':
        item = {
          thematic: thematic?.parentObject?.thematic,
          subThematic: thematic?.parentObject?.subThematic,
          subSubThematic: thematic?.thematicsOverviewDetails?.name,
        };
    }
    return item;
  };

  const setLevelThematicsData = () => {
    const subThematics: any[] = [];
    const subSubThematics: any[] = [];
    if (
      thematicsOverviewData !== null &&
      thematicsOverviewData !== undefined &&
      Object.keys(thematicsOverviewData).length > 0
    ) {
      switch (currentLevel.id) {
        case 'thematics':
          thematicsOverviewData?.getThematicsOverviewData?.thematics.map((thematic: any) => {
            thematic.thematicsOverviewDetails.absImpactSat =
              thematic.thematicsOverviewDetails.impactSat !== null
                ? Math.abs(thematic.thematicsOverviewDetails.impactSat)
                : null;
          });
          if (currentThematicSelection.thematics.length === 0) {
            setThematicsData(thematicsOverviewData?.getThematicsOverviewData?.thematics);
          }
          if (currentThematicSelection.thematics.length > 0) {
            const validThematics = new Set(
              filter(
                currentThematicSelection.thematics,
                (thematic: any) => thematic.subThematic === null && thematic.subSubThematic === null,
              ).map((thematic: any) => thematic.thematic),
            );
            const filteredThematics = filter(
              thematicsOverviewData?.getThematicsOverviewData?.thematics,
              (thematic: any) => validThematics.has(thematic.thematicsOverviewDetails.name),
            );
            setThematicsData(filteredThematics);
          }
          setTotalThematicsMentionCount(currentMentionCountWithoutNull);
          setFilterByLevelDone(!filterByLevelDone);
          break;
        case 'subThematics':
          thematicsOverviewData?.getThematicsOverviewData?.thematics.map(
            (thematic: { subThematics: any[]; thematicsOverviewDetails: { name: any } }) => {
              thematic.subThematics.map((subThematic: any) => {
                subThematic.thematicsOverviewDetails.absImpactSat =
                  subThematic.thematicsOverviewDetails.impactSat !== null
                    ? Math.abs(subThematic.thematicsOverviewDetails.impactSat)
                    : null;
                subThematics.push({
                  ...subThematic,
                  parentObject: { thematic: thematic?.thematicsOverviewDetails?.name },
                });
              });
            },
          );
          if (currentThematicSelection.thematics.length === 0) {
            setThematicsData(subThematics);
          }
          if (currentThematicSelection.thematics.length > 0) {
            const validCombinations = new Set(
              filter(
                currentThematicSelection.thematics,
                (thematic: any) => thematic.subSubThematic === null && thematic.subThematic !== null,
              ).map((thematic: any) => `${thematic.thematic}|${thematic.subThematic}`),
            );
            const filteredThematics = filter(subThematics, (thematic: any) => {
              const key = `${thematic.parentObject.thematic}|${thematic.thematicsOverviewDetails.name}`;
              return validCombinations.has(key);
            });
            setThematicsData(filteredThematics);
          }
          setTotalThematicsMentionCount(currentMentionCountWithoutNull);
          setFilterByLevelDone(!filterByLevelDone);
          break;
        case 'subSubThematics':
          thematicsOverviewData?.getThematicsOverviewData?.thematics.map(
            (thematic: { subThematics: any[]; thematicsOverviewDetails: { name: any } }) => {
              thematic.subThematics.map(
                (subThematic: { subSubThematics: any[]; thematicsOverviewDetails: { name: any } }) =>
                  subThematic?.subSubThematics.map((subSubThematic: any) => {
                    subSubThematic.thematicsOverviewDetails.absImpactSat =
                      subSubThematic.thematicsOverviewDetails.impactSat !== null
                        ? Math.abs(subSubThematic.thematicsOverviewDetails.impactSat)
                        : null;
                    subSubThematics.push({
                      ...subSubThematic,
                      parentObject: {
                        thematic: thematic?.thematicsOverviewDetails?.name,
                        subThematic: subThematic?.thematicsOverviewDetails?.name,
                      },
                    });
                  }),
              );
            },
          );
          if (currentThematicSelection.thematics.length === 0) {
            setThematicsData(subSubThematics);
          }
          if (currentThematicSelection.thematics.length > 0) {
            const validCombinations = new Set(
              currentThematicSelection.thematics
                .filter((thematic: any) => thematic.thematic && thematic.subThematic && thematic.subSubThematic)
                .map((thematic: any) => `${thematic.thematic}|${thematic.subThematic}|${thematic.subSubThematic}`),
            );
            const filteredThematics = filter(subSubThematics, (thematic: any) => {
              const key = `${thematic.parentObject.thematic}|${thematic.parentObject.subThematic}|${thematic.thematicsOverviewDetails.name}`;
              return validCombinations.has(key);
            });
            setThematicsData(filteredThematics);
          }
          setTotalThematicsMentionCount(currentMentionCountWithoutNull);
          setFilterByLevelDone(!filterByLevelDone);
      }
    }
  };

  const setOrderedData = () => {
    if (orderDirection !== false) {
      let sortOrder: string | Function = `thematicsOverviewDetails.${order}`;
      if (order === 'vb_percentage') {
        sortOrder = 'thematicsOverviewDetails.mentionCount';
      }
      if (order === 'absImpactSat') {
        sortOrder = (thematic: any) => Math.abs(thematic.thematicsOverviewDetails.absImpactSat ?? 0);
      }
      setThematicsData(orderBy(thematicsData, sortOrder, orderDirection));
    }
    if (orderDirection === false) {
      if (!filterByLevelDone) {
        setLevelThematicsData();
      }
      setFilterByLevelDone(false);
    }
  };

  useEffect(() => {
    if (thematicsData && thematicsData?.length > 0) {
      // check if at least some thematics have thematicsOverviewDetails.impactSat as number !== 0
      // if not : order = 'mentionCount' (orderDirection = 'desc' at initialization)
      setOrder('mentionCount');
      if (
        thematicsData.some(
          (thematic: any) =>
            thematic?.thematicsOverviewDetails?.impactSat !== null &&
            thematic?.thematicsOverviewDetails?.impactSat !== 0,
        )
      ) {
        // if so : order = 'absImpactSat' (orderDirection = 'desc' at initialization)
        setOrder('absImpactSat');
      }
    }
  }, [loading, thematicsData?.length]); // executes at first loading of the component and after response; or after level switch, or after thematic selection switch

  useEffect(() => {
    const filters = cloneDeep(filterKeys);
    filters.keywords =
      flatten(
        filter(filterKeys?.texts, (text: { name: string }) => text.name === 'keywords').map(
          (keyword: any[]) => keyword.values,
        ),
      ) || [];
    delete filters.texts;
    setQueryFilters(filters);
  }, [filterKeys]);

  useEffect(() => {
    if (
      thematicsOverviewData !== null &&
      thematicsOverviewData !== undefined &&
      Object.keys(thematicsOverviewData).length > 0
    ) {
      setLevelThematicsData();
      setTotalThematicsMentionCount(currentMentionCountWithoutNull);
    }
  }, [thematicsOverviewData]); // executes at response of query with thematics

  useEffect(() => {
    setOrderedData();
  }, [filterByLevelDone]); // executes after state is updated with data

  useEffect(() => {
    setLevelThematicsData();
  }, [currentLevel, currentThematicSelection]);

  useEffect(() => {
    setOrderedData();
  }, [order, orderDirection]);

  const toggle = () => setTooltipOpen(!tooltipOpen);

  const changeDirection = (prevOrder, currentOrder) => {
    if (orderDirection === false || prevOrder !== currentOrder) {
      setOrderDirection('asc');
    } else if (orderDirection === 'asc') {
      setOrderDirection('desc');
    } else if (orderDirection === 'desc') {
      setOrderDirection(false);
    }
  };

  useEffect(() => {
    const thematicSelectionDropdownOptions: ThematicSelectionType[] = selectionList.map(selection => ({
      ...selection,
      label: selection.name,
    }));
    setThematicSelectionOptions([allThematics, ...thematicSelectionDropdownOptions]);
    setCurrentThematicSelection(selectedThematicSelectionOption ?? allThematics);
  }, [projectId, selectionList]);

  return (
    <>
      {!isAiAvailable && (
        <Tooltip
          isOpen={tooltipOpen}
          target={targetId}
          toggle={toggle}
          autohide={false}
          placement="top"
          style={{ maxWidth: '320px', width: '320px' }}
        >
          <span>{t('card:Ai features expired', { aiUsageExpirationDate: new Date(aiUsageExpirationDate) })}</span>
          <img src="/apercu_features_ia.png" alt={t('AI features overview')} />
        </Tooltip>
      )}
      <SubHeaderContainer>
        <SelectorContainer>
          <StyledLabel>{t('button:Level to display')}</StyledLabel>
          <StyledDropdown
            direction="down"
            isOpen={isLevelDropdownOpen}
            toggle={() => {
              setLevelDropdownOpen(!isLevelDropdownOpen);
            }}
          >
            <StyledDropdownToggle>
              <StyledToggleButton disableHover bgColor="grayShades.g300">
                {t(currentLevel.label)}
                <Icon icon="INTERFACE_CHEVRON_DOWN" size={ImageSize.medium} />
              </StyledToggleButton>
            </StyledDropdownToggle>
            <Menu className="dataByFiltersDropdownMenu">
              {filtersToDisplay.map(item => {
                return (
                  <Item
                    disabled={currentLevel && item.id === currentLevel.id}
                    onClick={() => {
                      setCurrentLevel(item);
                      localStorage.setItem('landingPageAIIrritantsLevel', JSON.stringify(item));
                    }}
                  >
                    {t(item.label)}
                  </Item>
                );
              })}
            </Menu>
          </StyledDropdown>
        </SelectorContainer>
        <SelectorContainer>
          <StyledLabel>{t('card:Thematic selection')}</StyledLabel>
          <StyledDropdown
            direction="down"
            isOpen={isThematicSelectionDropdownOpen}
            toggle={() => {
              setThematicSelectionDropdownOpen(!isThematicSelectionDropdownOpen);
            }}
          >
            <StyledDropdownToggle>
              <StyledToggleButton disableHover bgColor="grayShades.g300">
                {t(currentThematicSelection.label)}
                <Icon icon="INTERFACE_CHEVRON_DOWN" size={ImageSize.medium} />
              </StyledToggleButton>
            </StyledDropdownToggle>
            <Menu className="dataByFiltersDropdownMenu">
              {thematicSelectionOptions.map(thematicSelection => {
                return (
                  <Item
                    disabled={currentThematicSelection && thematicSelection.id === currentThematicSelection.id}
                    onClick={() => {
                      setCurrentThematicSelection(thematicSelection);
                      dispatchSelectThematicSelection(thematicSelection);
                    }}
                  >
                    {t(thematicSelection.label)}
                  </Item>
                );
              })}
            </Menu>
          </StyledDropdown>
        </SelectorContainer>
      </SubHeaderContainer>
      <TableContainer>
        <Table borderless>
          <Head>
            <tr>
              <TitleCell>
                <div
                  role="button"
                  tabIndex={0}
                  style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                  onKeyDown={() => {}}
                  onClick={() => {
                    setOrder(prevState => {
                      changeDirection(prevState, 'name');
                      return 'name';
                    });
                  }}
                >
                  <span
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                    }}
                  >
                    <SortIcon
                      icon="INTERFACE_CHEVRON_UP"
                      isVisible={!orderDirection || (order === 'name' && orderDirection === 'asc')}
                      color={grayShades.g600}
                    />
                    <SortIcon
                      icon="INTERFACE_CHEVRON_DOWN"
                      isVisible={orderDirection === false || (order === 'name' && orderDirection === 'desc')}
                      color={grayShades.g600}
                    />
                  </span>
                  <span>{t('header:Triggers')}</span>
                </div>
              </TitleCell>
              <IndicatorCell>
                <div
                  role="button"
                  tabIndex={0}
                  style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                  onKeyDown={() => {}}
                  onClick={() => {
                    setOrder(prevState => {
                      changeDirection(prevState, 'absImpactSat');
                      return 'absImpactSat';
                    });
                  }}
                >
                  <span
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                    }}
                  >
                    <SortIcon
                      icon="INTERFACE_CHEVRON_UP"
                      isVisible={!orderDirection || (order === 'absImpactSat' && orderDirection === 'asc')}
                      color={grayShades.g600}
                    />
                    <SortIcon
                      icon="INTERFACE_CHEVRON_DOWN"
                      isVisible={orderDirection === false || (order === 'absImpactSat' && orderDirection === 'desc')}
                      color={grayShades.g600}
                    />
                  </span>
                  <span>{t('card:Impact CSAT')}</span>
                </div>
              </IndicatorCell>
              <IndicatorCell>
                <div
                  role="button"
                  tabIndex={0}
                  style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                  onKeyDown={() => {}}
                  onClick={() => {
                    setOrder(prevState => {
                      changeDirection(prevState, 'mentionCount');
                      return 'mentionCount';
                    });
                  }}
                >
                  <span
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                    }}
                  >
                    <SortIcon
                      icon="INTERFACE_CHEVRON_UP"
                      isVisible={!orderDirection || (order === 'mentionCount' && orderDirection === 'asc')}
                      color={grayShades.g600}
                    />
                    <SortIcon
                      icon="INTERFACE_CHEVRON_DOWN"
                      isVisible={orderDirection === false || (order === 'mentionCount' && orderDirection === 'desc')}
                      color={grayShades.g600}
                    />
                  </span>
                  <span>{t('card:Nb mentions')}</span>
                </div>
              </IndicatorCell>
              <IndicatorCell>
                <div
                  role="button"
                  tabIndex={0}
                  style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                  onKeyDown={() => {}}
                  onClick={() => {
                    setOrder(prevState => {
                      changeDirection(prevState, 'vb_percentage');
                      return 'vb_percentage';
                    });
                  }}
                >
                  <span
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                    }}
                  >
                    <SortIcon
                      icon="INTERFACE_CHEVRON_UP"
                      isVisible={!orderDirection || (order === 'vb_percentage' && orderDirection === 'asc')}
                      color={grayShades.g600}
                    />
                    <SortIcon
                      icon="INTERFACE_CHEVRON_DOWN"
                      isVisible={orderDirection === false || (order === 'vb_percentage' && orderDirection === 'desc')}
                      color={grayShades.g600}
                    />
                  </span>
                  <span>{t('card:Verbatim percentage')}</span>
                </div>
              </IndicatorCell>
              <IndicatorCell>
                <img
                  src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADAAAAAwCAYAAABXAvmHAAAACXBIWXMAAAsTAAALEwEAmpwYAAADIElEQVR4nO1YTWtTQRSdIvhRFesXWAuCgpuCCIILa2ptmryZV2MbKmntyn0hNu9OgsVNduq+/ooutCtBLLyZebEgBKTqyv6F6i+wRG7bpM/0tXkfk5DIO3Ah0MzknNt775wZQmLEiOELJSpGOVObGMCcBOk1AJUbnKkaBn4mvQRuymSdfD0sQ4yTXgEwtdosgFP5nvQCwBBpoHL7oAD1p0idFOlWWIYYxyx7kt/vhW38TleVU4mKUXfD+g1cg2t9/UZK3ARTZcFQmZe0OqhVwN6orIWMzVb7Lz1yznOmngJV8xhFo/Ikzz6c0CYAmPoZVgCubbW/ZVTG6+RhLwpU3tEowEmELaFWB1yBysFm8oBhyLnSyOpZ0t1NXOuzDMU8BdAdESOkHcBRiSPTa4wCUxN+9ylMfLpxKHmKYc9Dxr7UFhGYZY/sv/O7PpdbOVagcvpoAWo+SEKCl9OB/4Dz0O96ztRwS/J0N6zk2lBXmbml7JeLnDqzfgWAqbLlTLVfvwDmJILYaSwbzHwg8nQ3OBUzPC2vY+OTzqPW9zzlXOPMeRyUODRFiVUmca+OUS9O2leOHJUhg6dlEk/wthF/kaqe45PygW7i0DRmi6a6vzBmn9FKHo2Z29u0OzgVs3iiaxMAhp3tFHloiPA/wn0IUJlOCwCdjwqlVOUqWuLOCRBTi9P2ANGJcu7HcctwboeZ+eA/6znLFLfwfNFK/h8hmWo/Z+Ku1qY25Rzu+WzMPkk6BctYv8DNz0kdY7MU9p6QZ+oyMPWdU/UNP4cSklwb2rn7Bp0ypjAxCSQKgMo3Lu//OlJZUTEThPxKrhatzq3c+ilgasvlPn9HcYhozPyWjRU180ieM/XW48K+jH8LtyteJ+3WHsmIcK3EOsdScWfeQ8QWfidMTxx6oacN8nPlKD5np2EDvECE+Q2vJxXQ9bSC06bdAhanvw54nRFFHY9b7S6hhggq7h2YPEwNE13Ym0DLept4Hzz98XSBCpflEFPa7UHzGOVM/tJ50caGRksMzEloN2Z1YKm4sv+K9BryaCXwvZPKjSg1HyNGjBj/N/4CHLpRmzINzWUAAAAASUVORK5CYII="
                  alt="sparkling--v1"
                />
              </IndicatorCell>
              <IndicatorCell>
                {t('card:Enchantment')}
                <UpRightSpan>{t('card:AI')}</UpRightSpan>
              </IndicatorCell>
              <IndicatorCell>
                {t('card:Irritant')}
                <UpRightSpan>{t('card:AI')}</UpRightSpan>
              </IndicatorCell>
              <td>&nbsp;</td>
            </tr>
          </Head>
          <tbody>
            {loading && <Loader icon="INTERFACE_LOADING" />}
            {Object.keys(thematicsData).length > 0 &&
              thematicsData.map((thematic: unknown) => {
                const thematicMentionCount = thematic.thematicsOverviewDetails.mentionCount;
                const computedVerbatimPercentage = (thematicMentionCount / totalThematicsMentionCount) * 100;
                let verbatimPercentage = '0 %';
                if (!Number.isNaN(computedVerbatimPercentage)) {
                  verbatimPercentage = `${computedVerbatimPercentage.toFixed(2)} %`;
                }
                const modalTargetId = `tooltip-${thematic.thematicsOverviewDetails.name.replace(/\W/g, '_')}`;
                const thematicAnalyzed = getThematicAnalyzed(thematic);
                return (
                  <IrritantsAndEnchantingPointsAIRow
                    thematic={thematic}
                    verbatimPercentage={verbatimPercentage}
                    modalTargetId={modalTargetId}
                    isAiAvailable={isAiAvailable}
                    t={t}
                    thematicAnalyzed={thematicAnalyzed}
                    irritantsModal={irritantsModal}
                    enchantementsModal={enchantementsModal}
                    setIrritantsModal={setIrritantsModal}
                    setEnchantementsModal={setEnchantementsModal}
                    projectId={projectId}
                    range={range}
                    handleFetchClick={handleFetchClick}
                    shouldFetch={shouldFetch}
                    setShouldFetch={setShouldFetch}
                    thematicMentionCount={thematicMentionCount}
                    dataId={dataId}
                    queryFilters={queryFilters}
                    setTargetId={setTargetId}
                    setTooltipOpen={setTooltipOpen}
                  />
                );
              })}
          </tbody>
        </Table>
      </TableContainer>
    </>
  );
};

const mapStateToProps = (state: any) => ({
  filterKeys: get(state, ['filters', 'filterKeys']),
  projectId: get(state, ['projects', 'currentProject', 'id']),
  range: get(state, ['periods', 'currentPeriod']),
  aiUsageExpirationDate: get(state, ['auth', 'user', 'account', 'aiUsageExpirationDate']),
  selectionList: get(state, ['thematicSelections', 'list']),
  selectedThematicSelectionOption: get(state, ['thematicSelections', 'selectedThematicSelectionOption']),
  currentMentionCountWithoutNull: get(state, ['mentions', 'currentMentionCountWithoutNull']),
});

const mapDispatchToProps = (dispatch: any) => ({
  dispatchSelectThematicSelection: thematicSelection => {
    dispatch(selectThematicSelection(thematicSelection));
  },
});

export default withTranslation('filters', ['card', 'button', 'header'])(
  connect(mapStateToProps, mapDispatchToProps)(IrritantsAndEnchantingPointAIComputingCard),
);
